import { useMutation } from '@tanstack/react-query';
import urls from '../../utils/urls';
import useNotify from '../useNotify';
import { getIntl } from '../../utils/intl';
import { appAPI } from '../../utils/api';
import { useOrganizationId } from './useOrganization';
import { onNotesUpdate, onPersistVisitsToClientProfiles } from './cacheKeys';
import { APITypes } from '../../primary-record-types';

const intl = getIntl('UsePersistVisitToClientProfiles', {
    success: 'Successfully shared visit with clients',
    successSingular: 'Successfully shared visit with client',
    error: 'Failed to send visit to clients',
    failedToSendToClients:
        'Failed to send visit to {count} client(s). Your organization may not have proper permissions to some of the selected clients.',
    noClientsSelected: 'Select at least one client to share this visit with.',
});

export type UsePersistVisitToClientProfilesArgs = {
    onPersistSuccess?: () => void;
};

export type UsePersistVisitToClientProfilesReturnValue = {
    persistVisitToClientProfiles: (
        args: APITypes.organizationService.PersistVisitToOrganizationMedicalProfilesRequest,
    ) => void;
    isPersisting: boolean;
};

export default function usePersistVisitToClientProfiles({
    onPersistSuccess,
}: UsePersistVisitToClientProfilesArgs = {}): UsePersistVisitToClientProfilesReturnValue {
    const notify = useNotify();
    const organizationId = useOrganizationId();
    const persistMutation = useMutation({
        mutationFn: async (args: APITypes.organizationService.PersistVisitToOrganizationMedicalProfilesRequest) => {
            return appAPI.post<APITypes.organizationService.PersistVisitToOrganizationMedicalProfilesResponse>(
                urls.api.organizations.visits.persistToMedicalProfiles(organizationId),
                {
                    body: args,
                },
            );
        },
        onSuccess: (data, args) => {
            onPersistVisitsToClientProfiles.resetCache({ clientProfileIds: args.destinationMedicalProfileIds })();
            // The attached note is created server-side on each destination profile, bypassing the
            // note mutations that normally reset the notes caches — reset them per profile or the
            // clients' notes lists/counts stay stale.
            if (args.sourceNoteId)
                args.destinationMedicalProfileIds.forEach((medicalProfileId) =>
                    onNotesUpdate.resetCache({ medicalProfileId })(),
                );
            if (data.noAccessMedicalProfileIds?.length) {
                notify('warn', intl('failedToSendToClients', { count: data.noAccessMedicalProfileIds.length }));
            } else {
                notify('success', intl(args.destinationMedicalProfileIds.length === 1 ? 'successSingular' : 'success'));
            }
            onPersistSuccess?.();
        },
        onError: () => {
            notify('error', intl('error'));
        },
    });

    const persistVisitToClientProfiles = (
        args: APITypes.organizationService.PersistVisitToOrganizationMedicalProfilesRequest,
    ) => {
        // Guard against an empty destination list: the API requires at least one
        // (destinationMedicalProfileIds min 1), and rows can be selected while still resolving
        // to zero medical-profile ids (e.g. invited clients with no profile yet). Notify rather
        // than POST an array the server will reject.
        if (args.destinationMedicalProfileIds.length === 0) {
            notify('warn', intl('noClientsSelected'));
            return;
        }
        persistMutation.mutate(args);
    };

    return {
        persistVisitToClientProfiles,
        isPersisting: persistMutation.isPending,
    };
}
